import { useState } from 'react'
import { supabase, readableError } from '../lib/supabase'

/**
 * Deleting an account, as a row in the account group rather than a loose
 * button under it.
 *
 * The three steps stay in one place — prompt is a row like every other row on
 * the screen; confirm and pending expand INSIDE the group as a .rowbody, so
 * the panel is visibly part of the row that opened it. Previously each step
 * rendered its own `borderTop: 1px solid var(--outline)` wrapper, and
 * --outline is not a token in this system (nor is --muted, used below it), so
 * both declarations were invalid and the rules they were drawing simply
 * didn't exist.
 */
export default function AccountDelete({ profile, onDeleted }) {
  const [step, setStep] = useState('prompt') // prompt | confirm | pending | error
  const [confirmEmail, setConfirmEmail] = useState('')
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  const startDelete = async () => {
    if (!confirmEmail.trim() || confirmEmail !== profile.email) {
      setErr('Email doesn\'t match. Type it exactly as shown to confirm.')
      return
    }

    setLoading(true)
    setErr(null)

    try {
      // Send confirmation email via edge function
      const { data, error } = await supabase.functions.invoke('send-delete-account-email', {
        body: { user_id: profile.user_id || profile.id },
      })

      if (error) throw error
      setStep('pending')
      setConfirmEmail('')
    } catch (e) {
      setErr(readableError(e))
      setLoading(false)
    }
  }

  if (step === 'prompt') {
    return (
      <button className="action destroy" onClick={() => setStep('confirm')}>
        <span>Delete my account<small>Held for 90 days before anything is erased</small></span>
        <span className="ar">›</span>
      </button>
    )
  }

  if (step === 'pending') {
    return (
      <div className="rowbody">
        <p className="small">
          <strong>Check your email.</strong> We've sent you a confirmation link.
          Click it to permanently delete your account and all its data.
        </p>
        <p className="small" style={{ marginTop: 8 }}>
          Account suspensions are held for 90 days. If you change your mind before
          then, sign back in and we can restore it.
        </p>
        <div className="rowbody-acts">
          <button className="btn btn-quiet" onClick={() => setStep('prompt')}>Cancel</button>
        </div>
      </div>
    )
  }

  return (
    <div className="rowbody">
      <p className="small">
        <strong>Delete your account?</strong> Your songs will stay safe in Beseter
        for 90 days. After that, we'll permanently delete everything. You can sign
        back in anytime during that window and restore your account.
      </p>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label small" htmlFor="del-confirm">
          Type your email to confirm: <strong>{profile.email}</strong>
        </label>
        <input
          id="del-confirm"
          className="input"
          type="email"
          autoComplete="off"
          placeholder="Type your email address"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          disabled={loading}
        />
      </div>

      {err && <p className="err" style={{ marginTop: 8 }}>{err}</p>}

      <div className="rowbody-acts">
        <button
          className="btn btn-danger"
          onClick={startDelete}
          disabled={!confirmEmail.trim() || loading}
        >
          {loading ? 'Sending…' : 'Delete my account'}
        </button>
        <button
          className="btn btn-quiet"
          onClick={() => { setStep('prompt'); setErr(null) }}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
